// Recording a take: a 3-2-1 count-in, then the mic with a level meter and timer.
// (Filming your face while recording, to puppet the cartoon's face, was removed; see docs/future-features.md.)
import { useEffect, useRef, useState } from "react";
import { MicRecorder } from "../../audio/playback";

type RecPhase = { phase: "idle" } | { phase: "count"; n: number } | { phase: "recording"; start: number };

export function useRecorder(onTake: (blob: Blob, seconds: number) => void, onError: (m: string) => void) {
  const mic = useRef(new MicRecorder());
  const timers = useRef<ReturnType<typeof setTimeout>[]>([]);
  const [rec, setRec] = useState<RecPhase>({ phase: "idle" });
  const [levels, setLevels] = useState<number[]>(Array(14).fill(0));
  const [elapsed, setElapsed] = useState(0);

  useEffect(() => {
    if (rec.phase !== "recording") return;
    let raf = 0, last = 0;
    const loop = (now: number) => {
      if (now - last > 50) {
        last = now;
        setLevels(v => [...v.slice(1), mic.current.level()]);
        setElapsed((now - rec.start) / 1000);
      }
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(raf);
  }, [rec]);
  useEffect(() => () => { timers.current.forEach(clearTimeout); }, []);

  async function start() {
    try { await mic.current.open(); } catch { return onError("Microphone blocked. Allow mic access for this page and try again."); }
    setRec({ phase: "count", n: 3 });
    timers.current = [
      setTimeout(() => setRec({ phase: "count", n: 2 }), 700),
      setTimeout(() => setRec({ phase: "count", n: 1 }), 1400),
      setTimeout(() => {
        mic.current.start();
        setLevels(Array(14).fill(0));
        setElapsed(0);
        setRec({ phase: "recording", start: performance.now() });
      }, 2100),
    ];
  }
  function cancel() {
    timers.current.forEach(clearTimeout);
    if (rec.phase === "recording") void mic.current.stop();
    setRec({ phase: "idle" });
  }
  async function finish() {
    if (rec.phase !== "recording") return cancel();
    const seconds = (performance.now() - rec.start) / 1000;
    const blob = await mic.current.stop();
    setRec({ phase: "idle" });
    if (blob.size < 500 || seconds < 0.3) return onError("That was too short. Tap record and say a line.");
    onTake(blob, seconds);
  }
  // One button: start, cancel the count-in, or stop.
  const press = () => (rec.phase === "idle" ? void start() : rec.phase === "recording" ? void finish() : cancel());
  return { rec, levels, elapsed, press, cancel, busy: rec.phase !== "idle" };
}
